import type { LbImage, LbPart, LbPost, LbPostData } from './types';
import { PartType } from './types';

export const sortFunction = (postA: LbPost, postB: LbPost) => {
  if (postA?.liveblogPostData?.isSticky && !postB?.liveblogPostData?.isSticky) return -1;
  if (!postA?.liveblogPostData?.isSticky && postB?.liveblogPostData?.isSticky) return 1;
  return Date.parse(postB.lastUpdateDate) - Date.parse(postA.lastUpdateDate);
};

export function time(dateString: string): string {
  const date = new Date(dateString);
  return date.toTimeString().substring(0, 5);
}

export function stripHtml(str: string): string {
  return str?.replace(/<\/?[^>]+(>|$)/g, '') ?? '';
}

const resolvePathUrl = (url: string, format: string, baseImgUrl: string) => {
  const arr = url.split('/');
  arr.splice(3, 0, format);
  return baseImgUrl + arr.join('/');
};

export function getImageObject(imgs, baseImgUrl: string) {
  const imgObj: LbImage = {
    figcredits: imgs.figcredits ?? '',
    figcaption: imgs.figcaption ?? '',
  };
  imgs.img.forEach((i) => {
    switch (i['emk-softCrop']) {
      case 'Wide':
        imgObj.wide = {
          w1: resolvePathUrl(i.src, 'format/wide1', baseImgUrl),
          w2: resolvePathUrl(i.src, 'format/wide2', baseImgUrl),
          w3: resolvePathUrl(i.src, 'format/wide3', baseImgUrl),
          w4: resolvePathUrl(i.src, 'format/wide4', baseImgUrl),
        };
        break;
      case 'Square':
        imgObj.square = {
          s1: resolvePathUrl(i.src, 'format/square1', baseImgUrl),
          s2: resolvePathUrl(i.src, 'format/square2', baseImgUrl),
          s3: resolvePathUrl(i.src, 'format/square3', baseImgUrl),
          s4: resolvePathUrl(i.src, 'format/square4', baseImgUrl),
        };
        break;
      case 'Portrait':
        imgObj.portrait = {
          p1: resolvePathUrl(i.src, 'format/portrait1', baseImgUrl),
          p2: resolvePathUrl(i.src, 'format/portrait2', baseImgUrl),
          p3: resolvePathUrl(i.src, 'format/portrait3', baseImgUrl),
        };
        break;
      case 'Ultrawide':
        imgObj.ultrawide = {
          ultrawide: resolvePathUrl(i.src, 'format/ultrawide', baseImgUrl),
        };
        break;
      case 'Free':
        imgObj.free = {
          f1: resolvePathUrl(i.src, 'format/free1', baseImgUrl),
          f2: resolvePathUrl(i.src, 'format/free2', baseImgUrl),
          f3: resolvePathUrl(i.src, 'format/free3', baseImgUrl),
          f4: resolvePathUrl(i.src, 'format/free4', baseImgUrl),
        };
        break;
    }
  });
  return imgObj;
}

export function getResourceId(part): string {
  return part?._attributes?.['data-id'];
}

export function isMatchEvent(liveblogPostData: LbPostData) {
  return liveblogPostData?.lbPostType === 'MATCH_EVENT';
}

export function getDataSource(part, liveblogPostData: LbPostData): { datasource: PartType; dataId: string } {
  let datasource: PartType = PartType.COMPOSER;
  if (['video', 'article', 'promo'].includes(part._attributes?.['emk-class'])) datasource = PartType.UEFA_EDITORIAL;
  if (['poll'].includes(part._attributes?.['emk-class'])) datasource = PartType.POLL;
  if (['card'].includes(part._attributes?.['emk-class'])) datasource = PartType.CARD;
  else if (['FREE-PHOTO'].includes(part._type)) datasource = PartType.FREE_PHOTO;
  else if (['VIDEO-POST'].includes(part._type)) datasource = PartType.VIDEO_POST;
  else if (['SOCIAL'].includes(part._type)) datasource = PartType.SOCIAL;
  else if (['MATCH_INFO', 'FORM_GUIDE', 'HEAD_TO_HEAD', 'LINEUP'].includes(liveblogPostData?.lbPostType))
    datasource = PartType.MATCH_SUMMARY;
  else if (isMatchEvent(liveblogPostData)) datasource = PartType.MATCH_EVENTS;
  const dataId = getResourceId(part);
  return { datasource, dataId };
}

export function getUrl(node): string {
  const url = node?.attributes?.webReference;
  const promoLink = node?.files['content.json']?.data?.document?.content?.find(
    (el) => el._attributes?.['emk-type'] === 'promo-link',
  )?._attributes?.href;

  return promoLink ?? url;
}

export const getHideFromWeb = (node): boolean => node?.attributes?.hideFromWeb;

export const getSection = (node): string => node?.attributes?.main?.section;

export const getKind = (node): string => node?.attributes?.main?.kind;

export function getPostTypes(parts: LbPart[], liveblogPostData): PartType[] {
  const types = parts.map((x) => x.datasource).filter(Boolean);
  if (isMatchEvent(liveblogPostData)) types.push(PartType.MATCH_EVENTS);
  return types;
}

export const formatString = (s: string) => {
  s = stripHtml(s);
  if (s.length <= 105) return s;
  s = `${s.substring(0, 105)}...`;
  return s;
};
